// ─── Money ──────────────────────────────────────────────────────────
// Costs cross the wire as integers — micro-dollars for spend, cents for the
// budget — so nothing accumulates float error on the way here. Dollars exist
// only for display and for the moment a rate is typed into an input.

const MICROS_PER_USD = 1_000_000;
const CENTS_PER_USD = 100;

/**
 * Rates are entered per million tokens, which is how every provider publishes
 * them. Entering $0.00000125 per token instead would be four zeroes away from
 * a tenfold mistake nobody would spot in a table.
 */
export const microsToUsd = (micros: number): number => micros / MICROS_PER_USD;
export const usdToMicros = (usd: number): number => Math.round(usd * MICROS_PER_USD);
export const centsToUsd = (cents: number): number => cents / CENTS_PER_USD;
export const usdToCents = (usd: number): number => Math.round(usd * CENTS_PER_USD);

/**
 * Spend, to the cent. Real spend under a cent reads as `<$0.01` rather than
 * `$0.00`, which would say the calls were free when they were merely cheap.
 */
export function formatUsd(usd: number, locale?: string): string {
  if (usd > 0 && usd < 0.01) return "<$0.01";
  return usd.toLocaleString(locale ?? "en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export const formatMicrosAsUsd = (micros: number, locale?: string): string =>
  formatUsd(microsToUsd(micros), locale);

/**
 * A rate, which needs more precision than spend: cache reads run to cents per
 * million and would all render as `$0.00` at two decimals. Trailing zeroes are
 * dropped so a whole-dollar rate does not read as spurious precision.
 */
export function formatRateUsd(micros: number): string {
  const usd = microsToUsd(micros);
  if (usd === 0) return "$0";
  return `$${usd.toFixed(4).replace(/\.?0+$/, "")}`;
}
